
import { useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchClips } from "@/utils/supabase/dataFetching";
import { staticChallenges } from "@/data/challengesData";
import { useDerivativesFormatter } from "@/hooks/useDerivativesFormatter";
import { useChannelsFormatter } from "@/hooks/useChannelsFormatter";
import { usePlaylistsFormatter } from "@/hooks/usePlaylistsFormatter";
import { useClipsFormatter } from "@/hooks/useClipsFormatter";
import { BatchedApiService } from "@/api/batchedApi";
import { derivativesApi } from "@/api/derivatives";

export const useHomeContent = () => {
  const queryClient = useQueryClient();
  const { formatClipsCallback } = useClipsFormatter();

  // Consolidated query using GraphQL-like batched API for everything except derivatives
  const batchedContentQuery = useQuery({
    queryKey: ['homeContentBatch'],
    queryFn: () => BatchedApiService.fetchHomeContentBatch(8),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000,    // 15 minutes
  });

  // Use direct Supabase query for derivatives to ensure correct view counts
  const derivativesQuery = useQuery({
    queryKey: ['homeLatestDerivatives'],
    queryFn: () => derivativesApi.getLatestDerivatives({ limit: 8 }),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000,    // 15 minutes
  });

  // Debug the derivatives data to ensure view counts are coming through
  useEffect(() => {
    if (derivativesQuery.data?.data?.length > 0) {
      console.log("ContentLoader - Direct derivatives query results:", 
        derivativesQuery.data.data.map(d => ({
          id: d.id,
          title: d.title?.substring(0, 20),
          views: typeof d.views === 'number' ? d.views : `Invalid type: ${typeof d.views}`,
          likesCount: d.likesCount,
          favoritesCount: d.favoritesCount,
          commentsCount: d.commentsCount
        }))
      );
    }
  }, [derivativesQuery.data]);

  const formattedChannels = useChannelsFormatter(batchedContentQuery.data?.channels);
  const formattedPlaylists = usePlaylistsFormatter(batchedContentQuery.data?.playlists);
  // Use the direct query results for derivatives
  const formattedDerivatives = useDerivativesFormatter(derivativesQuery.data?.data || []);
  
  const formattedClips = useMemo(() => {
    if (!batchedContentQuery.data) return [];
    
    const popularClips = formatClipsCallback(batchedContentQuery.data.popularClips || []);
    const recentClips = formatClipsCallback(batchedContentQuery.data.recentClips || []);
    
    const combinedClips = [...popularClips];
    const popularClipIds = new Set(combinedClips.map(clip => clip.id));
    
    recentClips.forEach(clip => {
      if (!popularClipIds.has(clip.id)) {
        combinedClips.push(clip);
      }
    });
    
    return combinedClips;
  }, [batchedContentQuery.data, formatClipsCallback]);

  const formattedCreators = [];

  useEffect(() => {
    if (batchedContentQuery.data?.playlists) {
      batchedContentQuery.data.playlists.forEach(playlist => {
        queryClient.prefetchQuery({
          queryKey: ['clips', playlist.id],
          queryFn: () => fetchClips(playlist.id),
          staleTime: 5 * 60 * 1000
        });
      });
    }
  }, [batchedContentQuery.data, queryClient]);

  return {
    loading: batchedContentQuery.isLoading || derivativesQuery.isLoading,
    channels: formattedChannels,
    playlists: formattedPlaylists,
    clips: formattedClips,
    derivatives: formattedDerivatives,
    challenges: batchedContentQuery.data?.challenges || staticChallenges,
    creators: formattedCreators
  };
};
